import React, { useEffect, useRef, useState } from "react";
import OpenSeadragon from "openseadragon";
import { Center } from "center";

export default function XrayViewer({ imageUrl }) {

  const viewerRef = useRef(null);
  const viewer = useRef(null);

  const [tool, setTool] = useState("wl");
  const [invert, setInvert] = useState(false);
  const [rotation, setRotation] = useState(0);
  const zoomAnchor = useRef(null);

  const contrast = useRef(1);
  const brightness = useRef(1);

  const start = useRef({ x: 0, y: 0 });

  // Initialize viewer
  useEffect(() => {

    viewer.current = OpenSeadragon({

      element: viewerRef.current,
      prefixUrl: "https://openseadragon.github.io/openseadragon/images/",
      showNavigator: false,
      showNavigationControl: false,

      smoothTileEdgesMinZoom: 1.1,
      immediateRender: true,
      blendTime: 0.1,

      animationTime: 0.6,
      springStiffness: 5,
      zoomPerClick: 1.3,   // default ~2 → too aggressive
      minZoomImageRatio: 0.7,
      maxZoomPixelRatio: 3,

      visibilityRatio: 0, // 0:allow full free movement, 1: forces 100% image always visible
      constrainDuringPan: false, // false:remove boundary restriction, true: prevents moving outside, 

      gestureSettingsMouse: {
        clickToZoom: false,
        dblClickToZoom: true,
        scrollToZoom: true,
        dragToPan: true
      },

      gestureSettingsTouch: {
        pinchToZoom: true,
        dblTapToZoom: true,
        dragToPan: true,
        flickEnabled: false,
        flickMinSpeed: 20,
        flickMomentum: 0.3
      }


    });

    viewer.current.addHandler("open", () => {

      setTimeout(() => {

        viewer.current.viewport.goHome(true);
        viewer.current.viewport.setRotation(0);
        viewer.current.viewport.applyConstraints();
        viewer.current.viewport.update();
        viewer.current.forceRedraw();

        setInvert(false);
        setRotation(0);
        brightness.current = 1;
        contrast.current = 1; 
        applyFilter();

      }, 50);

    });

    return () => viewer.current.destroy();

  }, []);

  // Load image
  useEffect(() => {

    if (!viewer.current || !imageUrl) return;

    viewer.current.world.removeAll();
    setTool("wl");

    viewer.current.open({
      type: "image",
      url: imageUrl,
      buildPyramid: false
    });

  }, [imageUrl]);


  /* PAN enable only when pan tool */

  useEffect(() => {

    if (!viewer.current) return;

    viewer.current.setMouseNavEnabled(tool === "pan");

  }, [tool]);

  /* Apply Window Level */

  const applyFilter = () => {

    if (!viewer.current) return;

    const canvas = viewer.current.drawer.canvas;

    if (!canvas) return;

    canvas.style.filter = `
      brightness(${brightness.current})
      contrast(${contrast.current})
      ${invert ? "invert(1)" : ""}
    `;

  };

  const rotateLeft = () => {
  const newRotation = (rotation - 90) % 360;
  setRotation(newRotation);
  viewer.current.viewport.setRotation(newRotation);
};

const rotateRight = () => {
  const newRotation = (rotation + 90) % 360;
  setRotation(newRotation);
  viewer.current.viewport.setRotation(newRotation);
};

  /* Start drag */

  const startDrag = (x, y) => {

  start.current = { x, y };

  if (tool === "zoom" && viewer.current) {

    const viewport = viewer.current.viewport;

    zoomAnchor.current =
      viewport.pointFromPixel(
        new OpenSeadragon.Point(x, y)
      );

  }

};

  /* Drag logic */

  const moveDrag = (x, y) => {

    const dx = x - start.current.x;
    const dy = y - start.current.y;

    /* WINDOW LEVEL */

    if (tool === "wl") {

      contrast.current += dx * 0.002;
      brightness.current += dy * 0.002;

      if(contrast.current<0.4){
        contrast.current = 0.4;
      }
      else if(contrast.current>4){
        contrast.current = 4;
      }

      if(brightness.current<0.4){
        brightness.current = 0.4;
      }
      
      if(contrast.current>1.5 && brightness.current<0.6){
        brightness.current = 0.6;
      }

//var obj  = document.getElementById("lbl");
//obj.innerHTML = 'B:' + brightness.current.toString() + ', C:' + contrast.current.toString();
      applyFilter();

    }

    /* ULTRA SMOOTH DRAG ZOOM */

    if (tool === "zoom") {

  const viewport = viewer.current.viewport;

  const dy = y - start.current.y;

  // ultra smooth exponential zoom
  const zoomFactor = Math.exp(-dy * 0.01);

  viewport.zoomBy(
    zoomFactor,
    zoomAnchor.current
  );

  viewport.applyConstraints();

  start.current = { x, y };

}

    start.current = { x, y };

  };

  /* Mouse */

  const mouseDown = (e) => {

    startDrag(e.clientX, e.clientY);

    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mouseup", mouseUp);

  };

  const mouseMove = (e) => {

    moveDrag(e.clientX, e.clientY);

  };

  const mouseUp = () => {

    window.removeEventListener("mousemove", mouseMove);
    window.removeEventListener("mouseup", mouseUp);

  };

  /* Touch */

  const touchStart = (e) => {

    const t = e.touches[0];

    startDrag(t.clientX, t.clientY);

  };

  const touchMove = (e) => {

    const t = e.touches[0];

    moveDrag(t.clientX, t.clientY);

  };

  useEffect(() => {
    applyFilter();
  }, [invert]);

  /* Fit */

  const fitToScreen = () => {

    viewer.current.viewport.goHome(true);

  };

  /* Reset */

  const resetViewer = () => {

    const viewport = viewer.current.viewport;

    viewport.goHome(true);
    viewport.setRotation(0);

    brightness.current = 1;
    contrast.current = 1;

    setInvert(false);
    setRotation(0);

    applyFilter();

  };

  return (
    <div className="xray-viewer-container" style={{ background: "black" }}>

      {/* Toolbar */}
      <div
        style={{
          position: "absolute",
          zIndex: 1000,
          padding: 2,
          display: "flex",
          gap: 2
        }}
      >
        <button style={{width: "45px"}} 
          className={`tool-btn ${tool === "pan" ? "active" : ""}`}
          onClick={() => setTool("pan")}
        >
          PAN
        </button>

<level style={{display:"none"}} id="lbl" className="tool-btn">test</level>

        <button
          className={`tool-btn ${tool === "zoom" ? "active" : ""}`}
          onClick={() => setTool("zoom")}
        >
          ZOOM
        </button>

        <button style={{width: "35px"}} 
          className={`tool-btn ${tool === "wl" ? "active" : ""}`}
          onClick={() => setTool("wl")}
        >
          WL
        </button>

        <button title="Invert" className="tool-btn" style={{width: "30px"}} onClick={() => setInvert(!invert)}>
          ⇌
        </button>
        {/*<!-- 
        <button className="tool-btn" onClick={fitToScreen}>
          FIT
        </button>
        -->*/}
        <button title="Rotate Left" className="tool-btn" style={{width: "30px"}} onClick={rotateLeft}>
          ⟲
        </button>

        <button title="Rotate Right" className="tool-btn" style={{width: "30px"}} onClick={rotateRight}>
          ⟳
        </button>
        
        <button className="tool-btn" onClick={resetViewer}>
          RESET
        </button>
        
      </div>

      {/* Viewer */}

      <div
        ref={viewerRef}
        onMouseDown={mouseDown}
        onTouchStart={touchStart}
        onTouchMove={touchMove}
        style={{
          width: "100%",
          height: "100%"
        }}
      />

    </div>

  );

}